import { cn } from '@/utils'
import { CSSProperties, ReactNode, useMemo } from 'react'
import dayjs from 'dayjs'
import { PDFify } from '@/components/pdf/PDFify'
import logo from '@/assets/q20-logo5.png'
import { EditableField } from '@/components/core/EditableField'
import { useRoute } from 'wouter'

export const LETTER_PAGE_HEIGHT = 612
const LETTER_PAGE_WIDTH = 792
const CHROMIUM_PRINT_SCALE = 96 / 72
type DocumentTemplateProps = {
  id: string
  children: ReactNode
  lastUpdated?: string
  createdAt?: string
  title?: string
  versionName?: string
  order: number
  isLatestDocumentVersion?: boolean
  testId?: string
  onEditTitle?: (title: string) => void
  fullScreen?: boolean
  isReadOnly?: boolean
  height?: number
  parentRef?: any
  headerVisibility?: 'visible' | 'hidden'
  footerVisibility?: 'visible' | 'hidden'
}
export const DocumentTemplate = ({
  id,
  children,
  lastUpdated,
  createdAt,
  title,
  versionName,
  order,
  isLatestDocumentVersion,
  testId,
  fullScreen,
  onEditTitle,
  isReadOnly,
  height = 1,
  parentRef,
  headerVisibility = 'visible',
  footerVisibility = 'visible',
}: DocumentTemplateProps) => {
  const [isPrintPage] = useRoute(/print$/)
  const pageHeight = useMemo(() => LETTER_PAGE_HEIGHT * height, [height])
  const printPageWidth = LETTER_PAGE_WIDTH * CHROMIUM_PRINT_SCALE
  const printPageHeight = pageHeight * CHROMIUM_PRINT_SCALE
  const pageStyle = {
    height: pageHeight,
    boxShadow: isPrintPage
      ? 'none'
      : 'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
    '--q20-document-page-width': `${LETTER_PAGE_WIDTH}px`,
    '--q20-document-page-height': `${pageHeight}px`,
    '--q20-print-page-width': `${printPageWidth}px`,
    '--q20-print-page-height': `${printPageHeight}px`,
    '--q20-print-scale': `${CHROMIUM_PRINT_SCALE}`,
  } as CSSProperties

  return (
    <div
      id={id}
      ref={parentRef}
      className={cn(
        'relative bg-white flex flex-col justify-between h-[612px] w-[792px] print:!h-[var(--q20-print-page-height)] print:!w-[var(--q20-print-page-width)] [&:not(:last-child)]:print:break-after-page print:break-inside-avoid print:m-0 select-none',
        {
          'w-[792px]': !fullScreen,
          'w-full': fullScreen,
          'overflow-hidden': isPrintPage,
        },
      )}
      style={pageStyle}
      data-testid={`document-${title?.replace(/\s/g, '-')}`}
    >
      <PDFify id={id} order={order}>
        <div
          className={cn('flex flex-col flex-1 px-6 pt-6 h-full', {})}
          data-testid={testId}
        >
          <div
            className={cn(
              'relative z-[10] flex items-center justify-between mb-2',
              {
                invisible: headerVisibility === 'hidden',
              },
            )}
          >
            {title ? (
              <h1 className="text-lg font-semibold document-title max-w-[560px] text-wrap">
                {!isReadOnly && onEditTitle ? (
                  <EditableField value={title} onSaveHandler={onEditTitle} />
                ) : (
                  <span>{title}</span>
                )}
              </h1>
            ) : (
              <div></div>
            )}
            <div
              data-testid="createdUpdatedDate"
              className="flex flex-col self-end text-right text-xs"
            >
              {versionName && !isLatestDocumentVersion && (
                <div className="font-semibold">{versionName}</div>
              )}
              {typeof lastUpdated === 'string' && (
                <div className="text-gray-500">
                  Last updated: {dayjs(lastUpdated).format('MMM DD, YYYY')}
                </div>
              )}
              {typeof createdAt === 'string' && (
                <div className="text-gray-500">
                  Created: {dayjs(createdAt).format('MMM DD, YYYY')}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col flex-1">{children}</div>
          {height > 1 &&
            Array.from({ length: Math.floor(height) }).map((_, index) => (
              <div
                key={index}
                id={`sub-page-index-${index}`}
                className={cn('absolute w-full', {
                  ' border-b border-dashed border-gray-200': index !== 0,
                })}
                style={{ left: 0, top: LETTER_PAGE_HEIGHT * index }}
              />
            ))}
        </div>
        <div
          className={cn(
            'flex items-center justify-center text-xs text-center absolute bottom-0 left-0 right-0 pb-0 text-gray-300',
            {
              invisible: footerVisibility === 'hidden',
            },
          )}
        >
          Made with
          <img src={logo} style={{ height: 40 }} />
        </div>
      </PDFify>
    </div>
  )
}
